"use client";

import { cn } from "../../lib";

export type BarTargetPinIcon = "book-open" | "lock" | "target";

export interface BarTargetPinProps {
	/** Icon inside the white circle. */
	icon?: BarTargetPinIcon;
	className?: string;
	/** Circle diameter in px. */
	sizePx?: number;
	"data-testid"?: string;
}

/**
 * White circle + downward caret pin used on progress / favour bars
 * (same chrome as battle encounter target markers).
 */
export function BarTargetPin({
	icon = "target",
	className,
	sizePx = 28,
	"data-testid": dataTestId = "bar-target-pin",
}: BarTargetPinProps) {
	const iconPx = Math.round(sizePx * 0.55);
	const caretBorderX = Math.max(6, Math.round(sizePx * 0.28));
	const caretBorderT = Math.max(8, Math.round(sizePx * 0.36));

	return (
		<div
			className={cn(
				"pointer-events-none flex flex-col items-center",
				className,
			)}
			data-testid={dataTestId}
			data-icon={icon}
			aria-hidden
		>
			<div
				className="flex items-center justify-center rounded-full bg-white shadow-[0_10px_14px_rgba(0,0,0,0.45)]"
				style={{ width: sizePx, height: sizePx }}
			>
				<BarTargetPinGlyph icon={icon} sizePx={iconPx} />
			</div>
			<div
				className="-mt-1 h-0 w-0 border-x-transparent border-t-white"
				style={{
					borderLeftWidth: caretBorderX,
					borderRightWidth: caretBorderX,
					borderTopWidth: caretBorderT,
				}}
			/>
		</div>
	);
}

function BarTargetPinGlyph({
	icon,
	sizePx,
}: {
	icon: BarTargetPinIcon;
	sizePx: number;
}) {
	const common = {
		width: sizePx,
		height: sizePx,
		className:
			"pointer-events-none block shrink-0 text-black [transform:translate3d(0,0,0)] [backface-visibility:hidden]",
		strokeWidth: 2.25,
		"aria-hidden": true as const,
	};

	if (icon === "lock") {
		return <LockGlyph {...common} />;
	}
	if (icon === "book-open") {
		return <BookOpenGlyph {...common} />;
	}
	return <TargetGlyph {...common} />;
}

/** Inline SVG avoids pulling lucide into every consumer of this chrome. */
function LockGlyph({
	width,
	height,
	className,
	strokeWidth,
}: {
	width: number;
	height: number;
	className?: string;
	strokeWidth: number;
}) {
	return (
		<svg
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
			width={width}
			height={height}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={strokeWidth}
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
		>
			<rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
			<path d="M7 11V7a5 5 0 0 1 10 0v4" />
		</svg>
	);
}

function BookOpenGlyph({
	width,
	height,
	className,
	strokeWidth,
}: {
	width: number;
	height: number;
	className?: string;
	strokeWidth: number;
}) {
	return (
		<svg
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
			width={width}
			height={height}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={strokeWidth}
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
		>
			<path d="M12 7v14" />
			<path d="M3 18a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h5a4 4 0 0 1 4 4 4 4 0 0 1 4-4h5a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1h-6a3 3 0 0 0-3 3 3 3 0 0 0-3-3z" />
		</svg>
	);
}

function TargetGlyph({
	width,
	height,
	className,
	strokeWidth,
}: {
	width: number;
	height: number;
	className?: string;
	strokeWidth: number;
}) {
	return (
		<svg
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
			width={width}
			height={height}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={strokeWidth}
			strokeLinecap="round"
			strokeLinejoin="round"
			className={className}
		>
			<circle cx="12" cy="12" r="10" />
			<circle cx="12" cy="12" r="6" />
			<circle cx="12" cy="12" r="2" />
		</svg>
	);
}
